import { citizenHealthProfileShape } from '~/packages/publicHealth/citizenHealthProfile/stats/citizenHealthProfileStats.definition'
import { prepareAssertions } from '~/packages/general/variable/prepareAssertions.util'
import { Variable } from '~/packages/general/variable/variables.typing'
import { defineAction } from '~/prix'
import { analysis } from '~/prix/types/presets/analysis'
import { assertions } from '~/prix/types/presets/assertions'
import { select } from '~/prix/types/presets/select'
import { spaceForPoint } from '~/prix/types/presets/space'
import { timeForCurrentOnly } from '~/prix/types/presets/time'
import { variable } from '~/prix/types/presets/variable'
import prefixProperties from '~/prix/utils/prefixProperties'
import questionnaireCitizenHealthProfileStatsAction from './questionnaireCitizenHealthProfileStats.action.json' with { type: 'json' }
import { z } from 'zod'
import expression from '~/prix/types/expression'
import { getZodSchemaInfo } from '~/prix/types/zodType'
import { healthVariableBaseRelevance } from '~/packages/general/variable/healthVariableRelevance.util'
import { citizenLocationShape, citizenShapeBase } from '~/packages/publicHealth/citizen/stats/citizenShape'

export const questionnaireCitizenHealthProfileShape = z.object({
  questionId: z.number().describe('ID da pergunta'),
  answerId: z.number().describe('ID da resposta'),
  questionOptionId: z.string().describe('ID opção da pergunta'),
  questionOptionArray: z.array(z.string()).describe('Array de labels das opções da pergunta'),
  ...prefixProperties('citizen.', citizenShapeBase.shape),
  ...prefixProperties('citizen.', citizenLocationShape.shape),
  ...prefixProperties('citizenHealth.', citizenHealthProfileShape.shape)
})
export const questionnaireCitizenHealthProfileExpression = expression(
  questionnaireCitizenHealthProfileShape.shape,
)

export const questionnaireCitizenHealthProfileVariable: Variable = {
  key: 'questionnaireCitizenHealthProfile',
  actionDefinitionKey: questionnaireCitizenHealthProfileStatsAction.key,
  title: 'Cadastros individuais com a resposta',
  categoryKey: 'generalQuestionnaires',
  unitSingular: 'resposta',
  unitPlural: 'respostas',
  grammaticalGender: 'female',
  timeType: 'currentOnly',
  shape: getZodSchemaInfo(questionnaireCitizenHealthProfileShape),
  betterDensity: 'less',
  relevance: healthVariableBaseRelevance.questionnaireCitizenHealthProfile,
  basicSelect: ['citizen.name', 'citizen.socialName', 'citizen.birthDate', 'citizen.sex'],
  detailedSelect: [
    /* CIDADÃO */
    'citizen.cns',
    'citizen.cpf',
    'citizen.phoneNumber',
    'citizen.cns',
    'citizen.cpf',
    'citizen.streetName',
    'citizen.streetNumber',
    'citizen.neighborhood',
    'citizen.complement',
    'citizen.referencePoint',
    'citizen.postalCode',
    'citizen.deceased',
    'citizen.latitude',
    'citizen.longitude',

    /* SAÚDE */
    'citizenHealth.email',
    'citizenHealth.isResponsibleFamilyMember',
    'citizenHealth.hasDiabetes',
    'citizenHealth.hasHypertension',
    'citizenHealth.hasMedicalProfessionalDiagnosisOfMentalConditions',
    'citizenHealth.isPregnant',
    'citizenHealth.smoker',
    'citizenHealth.skinColorOrRace',
    'citizenHealth.educationLevel',
    'citizenHealth.employmentStatus',
    'citizenHealth.homeless',
    'citizenHealth.hasPrivateHealthInsurance',
    'citizenHealth.nationality',


  ],
  assertions: prepareAssertions(questionnaireCitizenHealthProfileExpression),
}

const questionnaireCitizenHealthProfileStatsDefinition = defineAction({
  ...questionnaireCitizenHealthProfileStatsAction,
  input: z.object({
    variable,
    highlight: questionnaireCitizenHealthProfileExpression.optional(),
    filter: questionnaireCitizenHealthProfileExpression.optional(),
    space: spaceForPoint().optional(),
    time: timeForCurrentOnly().optional(),
    select: select(questionnaireCitizenHealthProfileShape),
    assertions,
    analysis,
  }),
  output: z.any(),
  cache: input => {
    if (input.filter || input.space?.delimitationType === 'custom') {
      return {}
    }
    return {
      back: {
        expirationMsForFallback: 1000 * 60 * 60 * 24 * 7, // 7 days
        expirationMsForUpdate: 1000 * 60 * 60 * 2, // 2 hours
      },
      front: {
        expirationMsForFallback: 1000 * 60 * 60 * 24 * 7, // 7 days
        expirationMsForUpdate: 1000 * 60 * 60 * 2, // 2 hours
      },
    }
  },
})

export default questionnaireCitizenHealthProfileStatsDefinition
